<script setup lang="ts">
import { computed } from 'vue'

import { type Environment, useEnvironment } from '#app/composables/use-environment'
import { Button } from '#ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '#ui/select'
import { useSidebar } from '#ui/sidebar'
import { Tooltip, TooltipContent, TooltipTrigger } from '#ui/tooltip'

const { environment, isAcceptanceDeploy, setEnvironment } = useEnvironment()
const { state, isMobile } = useSidebar()

const isCollapsed = computed(() => state.value === 'collapsed' && !isMobile.value)
const initial = computed(() => (environment.value === 'production' ? 'P' : 'A'))
const tooltipLabel = computed(
  () =>
    `Environment: ${environment.value === 'production' ? 'Production' : 'Acceptance'}` +
    (isAcceptanceDeploy ? ' (backend locked to Acceptance)' : ''),
)

function onChange(value: unknown) {
  if (value === 'acceptance' || value === 'production') {
    setEnvironment(value as Environment)
  }
}

function toggle() {
  setEnvironment(environment.value === 'production' ? 'acceptance' : 'production')
}
</script>

<template>
  <div v-if="isCollapsed" class="flex h-8 justify-center px-1">
    <Tooltip>
      <TooltipTrigger as-child>
        <Button
          variant="outline"
          size="icon"
          :aria-label="tooltipLabel"
          class="text-xs font-semibold"
          :class="environment === 'production' ? 'text-primary' : 'text-muted-foreground'"
          @click="toggle"
        >
          {{ initial }}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right" align="center">{{ tooltipLabel }}</TooltipContent>
    </Tooltip>
  </div>
  <div v-else class="h-8 px-2">
    <Select :model-value="environment" @update:model-value="onChange">
      <SelectTrigger class="w-full" :aria-label="tooltipLabel" :title="tooltipLabel">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="acceptance">Acceptance</SelectItem>
        <SelectItem value="production">Production</SelectItem>
      </SelectContent>
    </Select>
  </div>
</template>
